import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Form } from "@/shared/ui/form";
import {
  ReceivableCurrency,
  ReceivableTaxType,
  PendingReceivableTax,
} from "../../utils/types";
import {
  taxFormSchema,
  TaxFormValues,
} from "./utils";
import { TaxFormFields } from "./TaxFormFields";
import { TaxDocumentForm } from "./TaxDocumentForm";
import { TaxDocumentList } from "./TaxDocumentList";
import { useTaxDocuments } from "./hooks/useTaxDocuments";
import { useTranslation } from "@/shared/i18n";

interface TaxCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    values: TaxFormValues,
    documents: PendingReceivableTax["documents"]
  ) => Promise<void>;
  disabled?: boolean;
}

export function TaxCreateDialog({
  open,
  onOpenChange,
  onSubmit,
  disabled = false,
}: TaxCreateDialogProps) {
  const { t } = useTranslation('receivables-components');
  const form = useForm<TaxFormValues>({
    resolver: zodResolver(taxFormSchema),
    defaultValues: {
      currency: ReceivableCurrency.BRL,
      type: ReceivableTaxType.IOF,
      description: "",
      document: "",
      value: 0,
    },
  });

  const documentsHook = useTaxDocuments();
  const isSubmitting = form.formState.isSubmitting;

  useEffect(() => {
    if (!open) {
      form.reset({
        currency: ReceivableCurrency.BRL,
        type: ReceivableTaxType.IOF,
        description: "",
        document: "",
        value: 0,
      });
      documentsHook.reset();
    }
  }, [open, form, documentsHook]);

  const handleSubmit = async (values: TaxFormValues) => {
    if (disabled || isSubmitting) return;
    await onSubmit(values, documentsHook.documents);
  };

  const isBusy = disabled || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('newTax')}</DialogTitle>
          <DialogDescription>{t('newTaxDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(handleSubmit)(e);
            }}
            className="space-y-4"
          >
            <TaxFormFields form={form} disabled={isBusy} />

            <TaxDocumentForm
              draft={documentsHook.draft}
              onDraftChange={documentsHook.updateDraft}
              onAdd={documentsHook.addDocument}
              error={documentsHook.error}
              disabled={isBusy}
              documentsCount={documentsHook.documents.length}
              fileInputRef={documentsHook.fileInputRef}
              acceptedTypes={documentsHook.acceptedTypes}
              onFileChange={documentsHook.handleFileChange}
              onFileButtonClick={() => documentsHook.handleFileButtonClick(isBusy)}
              onFileRemove={documentsHook.handleFileRemove}
            />

            {documentsHook.documents.length > 0 && (
              <TaxDocumentList
                documents={documentsHook.documents}
                onRemove={(doc) => documentsHook.removeDocument(doc.id)}
                disabled={isBusy}
                title={t('documents')}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenChange(false);
                }}
                disabled={isBusy}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isBusy || isSubmitting} onClick={(e) => e.stopPropagation()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('saving')}
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('newTax')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

